// src/store/appStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware'; // Optional: for devtools and persistence
void persist;
import type { AIModel, AppDefinition } from '../types/models'; // Type stubs for future use
import { availableAIModels, ChatSession, Project } from '../types/models'; // Import data
import type { Contact, Matter, IntakeSubmission } from '../config/api'; // Import API types
// Keep AIModel and AppDefinition stubs for future use
// Keep AIModel and AppDefinition type stubs for future use
// Type stub kept alive for future use
const _aiModelTypeStub: AIModel | undefined = undefined;
// Type stub kept alive for future use
const _appDefinitionTypeStub: AppDefinition | undefined = undefined;

// Define the shape of your store's state
interface AppState {
  // Authentication & Tenant
  currentFirmId: string | null; // Will be set after Keycloak login (realm name)
  currentUserId: string | null; // Keycloak user ID (sub claim)
  isAuthenticated: boolean;     // Derived from Keycloak

  // UI State
  isSidebarExpanded: boolean;
  collapsedGroups: string[]; // Persisted collapsed navigation groups
  
  // Application Data (will be fetched from backend)
  projects: Project[];
  chatSessions: ChatSession[]; // Could be all chats, or chats for current project
  
  // User Selections
  selectedAIModelId: string | null;
  currentProjectId: string | null;
  currentChatId: string | null; // ID of the chat currently active in ChatView

  // Contact Management State
  contacts: Contact[];
  selectedContactId: string | null;
  contactsLoading: boolean;
  contactsError: string | null;
  contactFilters: {
    status?: string;
    contact_type?: string;
    search?: string;
  };

  // Matter Management State
  matters: Matter[];
  selectedMatterId: string | null;
  mattersLoading: boolean;
  mattersError: string | null;
  matterFilters: {
    client_id?: string;
    status?: string;
    practice_area?: string;
    search?: string;
  };

  // Intake Management State
  intakeSubmissions: IntakeSubmission[];
  selectedSubmissionId: string | null;
  intakeLoading: boolean;
  intakeError: string | null;

  // Actions (functions to update the state)
  setCurrentFirmId: (firmId: string | null) => void;
  setCurrentUserId: (userId: string | null) => void;
  setIsAuthenticated: (authStatus: boolean) => void;
  
  toggleSidebar: () => void;
  setSidebarExpanded: (expanded: boolean) => void;

  // Navigation UI Actions
  toggleCollapsedGroup: (groupId: string) => void;
  setCollapsedGroups: (groups: string[]) => void;
  toggleAccordionGroup: (groupId: string, allGroupIds: string[]) => void;
  addCollapsedGroup: (groupId: string) => void;
  removeCollapsedGroup: (groupId: string) => void;

  setProjects: (projects: Project[]) => void;
  addProject: (project: Project) => void; // For optimistic updates

  setChatSessions: (sessions: ChatSession[]) => void;
  addChatSession: (session: ChatSession) => void; // For optimistic updates
  
  setSelectedAIModelId: (modelId: string | null) => void;
  setCurrentProjectId: (projectId: string | null) => void;
  setCurrentChatId: (chatId: string | null) => void;

  // Contact Actions
  setContacts: (contacts: Contact[]) => void;
  addContact: (contact: Contact) => void;
  updateContact: (id: string, updates: Partial<Contact>) => void;
  removeContact: (id: string) => void;
  setSelectedContactId: (id: string | null) => void;
  setContactsLoading: (loading: boolean) => void;
  setContactsError: (error: string | null) => void;
  setContactFilters: (filters: { status?: string; contact_type?: string; search?: string }) => void;

  // Matter Actions
  setMatters: (matters: Matter[]) => void;
  addMatter: (matter: Matter) => void;
  updateMatter: (id: string, updates: Partial<Matter>) => void;
  removeMatter: (id: string) => void;
  setSelectedMatterId: (id: string | null) => void;
  setMattersLoading: (loading: boolean) => void;
  setMattersError: (error: string | null) => void;
  setMatterFilters: (filters: { client_id?: string; status?: string; practice_area?: string; search?: string }) => void;

  // Intake Actions
  setIntakeSubmissions: (submissions: IntakeSubmission[]) => void;
  addIntakeSubmission: (submission: IntakeSubmission) => void;
  updateIntakeSubmission: (id: string, updates: Partial<IntakeSubmission>) => void;
  setSelectedSubmissionId: (id: string | null) => void;
  setIntakeLoading: (loading: boolean) => void;
  setIntakeError: (error: string | null) => void;

  // TODO: Add actions for fetching data from backend
  // fetchProjects: (firmId: string, userId: string) => Promise<void>;
  // fetchChatSessions: (firmId: string, userId: string, projectId?: string) => Promise<void>;
}

export const useAppStore = create<AppState>()(
  devtools( // Optional: for Redux DevTools integration
    // persist( // Optional: for persisting some state to localStorage (e.g., isSidebarExpanded)
      (set) => ({
        // Initial State
        currentFirmId: null,
        currentUserId: null,
        isAuthenticated: false,
        isSidebarExpanded: true,
        collapsedGroups: JSON.parse(localStorage.getItem('webelieve-collapsed-groups') || '["system-tools", "admin-tools"]'),
        projects: [],
        chatSessions: [],
        selectedAIModelId: availableAIModels.length > 0 ? availableAIModels[0].id : null,
        currentProjectId: null,
        currentChatId: null,

        // Contact State
        contacts: [],
        selectedContactId: null,
        contactsLoading: false,
        contactsError: null,
        contactFilters: {},

        // Matter State
        matters: [],
        selectedMatterId: null,
        mattersLoading: false,
        mattersError: null,
        matterFilters: {},

        // Intake State
        intakeSubmissions: [],
        selectedSubmissionId: null,
        intakeLoading: false,
        intakeError: null,

        // Actions
        setCurrentFirmId: (firmId) => set({ currentFirmId: firmId }),
        setCurrentUserId: (userId) => set({ currentUserId: userId }),
        setIsAuthenticated: (authStatus) => set({ isAuthenticated: authStatus }),

        toggleSidebar: () => set((state) => ({ isSidebarExpanded: !state.isSidebarExpanded })),
        setSidebarExpanded: (expanded) => set({ isSidebarExpanded: expanded }),

        // Navigation UI Actions
        toggleCollapsedGroup: (groupId) => set((state) => {
          const isCollapsed = state.collapsedGroups.includes(groupId);
          const newCollapsedGroups = isCollapsed 
            ? state.collapsedGroups.filter(id => id !== groupId) 
            : [...state.collapsedGroups, groupId];
          
          // Persist to localStorage
          localStorage.setItem('webelieve-collapsed-groups', JSON.stringify(newCollapsedGroups));
          
          return { collapsedGroups: newCollapsedGroups };
        }),
        setCollapsedGroups: (groups) => {
          localStorage.setItem('webelieve-collapsed-groups', JSON.stringify(groups));
          set({ collapsedGroups: groups });
        },

        // Accordion behavior: expand clicked group, collapse all others
        toggleAccordionGroup: (groupId, allGroupIds) => set((state) => {
          const isCurrentlyCollapsed = state.collapsedGroups.includes(groupId);
          
          if (isCurrentlyCollapsed) {
            // If clicking a collapsed group, expand it and collapse all others
            const newCollapsedGroups = allGroupIds.filter(id => id !== groupId);
            localStorage.setItem('webelieve-collapsed-groups', JSON.stringify(newCollapsedGroups));
            return { collapsedGroups: newCollapsedGroups };
          } else {
            // If clicking an expanded group, collapse it (optional behavior)
            const newCollapsedGroups = [...state.collapsedGroups, groupId];
            localStorage.setItem('webelieve-collapsed-groups', JSON.stringify(newCollapsedGroups));
            return { collapsedGroups: newCollapsedGroups };
          }
        }),
        addCollapsedGroup: (groupId) => set((state) => {
          const newCollapsedGroups = [...state.collapsedGroups, groupId];
          localStorage.setItem('webelieve-collapsed-groups', JSON.stringify(newCollapsedGroups));
          return { collapsedGroups: newCollapsedGroups };
        }),
        removeCollapsedGroup: (groupId) => set((state) => {
          const newCollapsedGroups = state.collapsedGroups.filter(id => id !== groupId);
          localStorage.setItem('webelieve-collapsed-groups', JSON.stringify(newCollapsedGroups));
          return { collapsedGroups: newCollapsedGroups };
        }),

        setProjects: (projects) => set({ projects }),
        addProject: (project) => set((state) => ({ projects: [...state.projects, project] })),
        
        setChatSessions: (sessions) => set({ chatSessions: sessions }),
        addChatSession: (session) => set((state) => ({ chatSessions: [...state.chatSessions, session] })),

        setSelectedAIModelId: (modelId) => set({ selectedAIModelId: modelId }),
        setCurrentProjectId: (projectId) => {
          set({ currentProjectId: projectId, currentChatId: null }); // Reset current chat when project changes
          // TODO: Potentially trigger fetch for chats of this project
          // get().fetchChatSessions(get().currentFirmId!, get().currentUserId!, projectId);
        },
        setCurrentChatId: (chatId) => set({ currentChatId: chatId }),

        // Contact Actions
        setContacts: (contacts) => set({ contacts, contactsError: null }),
        addContact: (contact) => set((state) => ({ 
          contacts: [...state.contacts, contact],
          contactsError: null
        })),
        updateContact: (id, updates) => set((state) => ({
          contacts: state.contacts.map(contact => 
            contact.id === id ? { ...contact, ...updates } : contact
          ),
          contactsError: null
        })),
        removeContact: (id) => set((state) => ({
          contacts: state.contacts.filter(contact => contact.id !== id),
          selectedContactId: state.selectedContactId === id ? null : state.selectedContactId,
          contactsError: null
        })),
        setSelectedContactId: (id) => set({ selectedContactId: id }),
        setContactsLoading: (loading) => set({ contactsLoading: loading }),
        setContactsError: (error) => set({ contactsError: error, contactsLoading: false }),
        setContactFilters: (filters) => set({ contactFilters: filters }),

        // Matter Actions
        setMatters: (matters) => set({ matters, mattersError: null }),
        addMatter: (matter) => set((state) => ({ 
          matters: [...state.matters, matter],
          mattersError: null
        })),
        updateMatter: (id, updates) => set((state) => ({
          matters: state.matters.map(matter => 
            matter.id === id ? { ...matter, ...updates } : matter
          ),
          mattersError: null
        })),
        removeMatter: (id) => set((state) => ({
          matters: state.matters.filter(matter => matter.id !== id),
          selectedMatterId: state.selectedMatterId === id ? null : state.selectedMatterId,
          mattersError: null
        })),
        setSelectedMatterId: (id) => set({ selectedMatterId: id }),
        setMattersLoading: (loading) => set({ mattersLoading: loading }),
        setMattersError: (error) => set({ mattersError: error, mattersLoading: false }),
        setMatterFilters: (filters) => set({ matterFilters: filters }),

        // Intake Actions
        setIntakeSubmissions: (submissions) => set({ intakeSubmissions: submissions, intakeError: null }),
        addIntakeSubmission: (submission) => set((state) => ({ 
          intakeSubmissions: [...state.intakeSubmissions, submission],
          intakeError: null
        })),
        updateIntakeSubmission: (id, updates) => set((state) => ({
          intakeSubmissions: state.intakeSubmissions.map(submission => 
            submission.submissionId === id ? { ...submission, ...updates } : submission
          ),
          intakeError: null
        })),
        setSelectedSubmissionId: (id) => set({ selectedSubmissionId: id }),
        setIntakeLoading: (loading) => set({ intakeLoading: loading }),
        setIntakeError: (error) => set({ intakeError: error, intakeLoading: false }),

        // Example of an async action placeholder (actual implementation later)
        // fetchProjects: async (firmId, userId) => {
        //   if (!firmId || !userId) return;
        //   // const token = keycloak.token; // Need to get token from Keycloak instance
        //   // const response = await fetch(`/api/tenants/${firmId}/users/${userId}/projects`, { headers: {'Authorization': `Bearer ${token}`}});
        //   // const projects = await response.json();
        //   // set({ projects });
        //   console.log(`Placeholder: Fetching projects for firm ${firmId}, user ${userId}`);
        //   // Mock data for now:
        //   const mockProjects: Project[] = [
        //     {id: 'proj1', name: 'Project Alpha', createdAt: new Date(), updatedAt: new Date()},
        //     {id: 'proj2', name: 'Project Beta', createdAt: new Date(), updatedAt: new Date()},
        //   ];
        //   set({ projects: mockProjects });
        // },
        // fetchChatSessions: async (firmId, userId, projectId) => {
        //   // ... similar fetching logic ...
        //   console.log(`Placeholder: Fetching chats for firm ${firmId}, user ${userId}, project ${projectId}`);
        //   const mockChats: ChatSession[] = [
        //      {id: 'chat1', projectId: projectId, title: 'Chat about contracts', createdAt: new Date(), updatedAt: new Date()},
        //   ];
        //   set({ chatSessions: mockChats });
        // }
      }),
      // { name: 'webelieve-app-storage' } // Name for persist middleware if used
    // )
  )
);